import { Component } from "@theme/component";

/**
 * A custom element that displays a full-width countdown timer.
 *
 * Counts down to a specific date/time in Los Angeles timezone.
 * When expired, either shows an expired message or hides the component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} daysContainer - Container for days display
 * @property {HTMLElement} daysElement - Element showing number of days
 * @property {HTMLElement} hoursElement - Element showing number of hours
 * @property {HTMLElement} minutesElement - Element showing number of minutes
 * @property {HTMLElement} secondsElement - Element showing number of seconds
 * @property {HTMLElement} [expiredMessage] - Optional element to show when expired
 *
 * @extends {Component<Refs>}
 */
class FtsCountdownTimer extends Component {
  requiredRefs = ["hoursElement", "minutesElement", "secondsElement"];

  /** @type {number | undefined} */
  #timerInterval;

  /** @type {Date | null} */
  #targetDate = null;

  /**
   * Called when the element is connected to the document's DOM.
   */
  async connectedCallback() {
    await super.connectedCallback();
    this.#initializeCountdown();
  }

  /**
   * Called when the element is removed from the DOM.
   */
  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.#timerInterval) {
      clearInterval(this.#timerInterval);
    }
  }

  /**
   * Parses the countdown date from the data attribute.
   * Converts Los Angeles time to user's local time.
   *
   * @private
   * @returns {Date | null} The target date in user's timezone, or null if invalid
   */
  #parseCountdownDate() {
    const dateStr = this.dataset.endDate;
    if (!dateStr) return null;

    // Parse the date string (expected format: YYYY-MM-DD HH:MM)
    const [datePart, timePart] = dateStr.trim().split(" ");
    if (!datePart) return null;

    const [year, month, day] = datePart.split("-").map(Number);
    let hours = 23;
    let minutes = 59;

    if (timePart) {
      const [h, m] = timePart.split(":").map(Number);
      hours = h || 0;
      minutes = m || 0;
    }

    // Create an ISO string with LA timezone offset
    // We'll use a more reliable method: create the date string with timezone
    const laDateStr = `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}T${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}:59`;

    // Create date in LA timezone and convert to local
    const formatter = new Intl.DateTimeFormat("en-US", {
      timeZone: "America/Los_Angeles",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: false,
    });

    // Get current LA time to calculate offset
    const now = new Date();
    const laParts = formatter.formatToParts(now);
    const laObj = {};
    for (const part of laParts) {
      laObj[part.type] = part.value;
    }

    // Create date object for "now" in LA timezone representation
    const nowInLARepresentation = new Date(
      `${laObj.year}-${laObj.month}-${laObj.day}T${laObj.hour}:${laObj.minute}:${laObj.second}`,
    );

    // Calculate the offset between actual now and LA representation
    const offset = now.getTime() - nowInLARepresentation.getTime();

    // Target date as if it were local time
    const targetAsLocal = new Date(laDateStr);

    // Apply offset to get actual target in user's timezone
    return new Date(targetAsLocal.getTime() + offset);
  }

  /**
   * Initializes the countdown timer.
   *
   * @private
   */
  #initializeCountdown() {
    if (this.#timerInterval) {
      clearInterval(this.#timerInterval);
      this.#timerInterval = undefined;
    }

    this.#targetDate = this.#parseCountdownDate();

    if (!this.#targetDate) {
      this.#handleExpired();
      return;
    }

    this.#updateCountdown();
    this.#timerInterval = setInterval(() => this.#updateCountdown(), 1000);
  }

  /**
   * Updates the countdown display.
   *
   * @private
   */
  #updateCountdown() {
    if (!this.#targetDate) return;

    const now = new Date();
    const timeDiff = this.#targetDate.getTime() - now.getTime();

    if (timeDiff <= 0) {
      this.#handleExpired();
      return;
    }

    const days = Math.floor(timeDiff / (1000 * 60 * 60 * 24));
    const hours = Math.floor(
      (timeDiff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60),
    );
    const minutes = Math.floor((timeDiff % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((timeDiff % (1000 * 60)) / 1000);

    const {
      daysContainer,
      daysElement,
      hoursElement,
      minutesElement,
      secondsElement,
    } = this.refs;

    // Show/hide days based on whether there are days remaining
    if (daysContainer && daysElement) {
      if (days > 0) {
        daysContainer.style.display = "";
        daysElement.textContent = String(days).padStart(2, "0");
        // Show the separator after days
        const separator = daysContainer.nextElementSibling;
        if (separator?.classList.contains("fts-countdown-timer__separator")) {
          separator.style.display = "";
        }
      } else {
        daysContainer.style.display = "none";
        const separator = daysContainer.nextElementSibling;
        if (separator?.classList.contains("fts-countdown-timer__separator")) {
          separator.style.display = "none";
        }
      }
    }

    if (hoursElement) hoursElement.textContent = String(hours).padStart(2, "0");
    if (minutesElement)
      minutesElement.textContent = String(minutes).padStart(2, "0");
    if (secondsElement)
      secondsElement.textContent = String(seconds).padStart(2, "0");
  }

  /**
   * Handles countdown expiration.
   *
   * @private
   */
  #handleExpired() {
    if (this.#timerInterval) {
      clearInterval(this.#timerInterval);
      this.#timerInterval = undefined;
    }

    const { expiredMessage } = this.refs;

    if (expiredMessage) {
      // Show expired message, hide countdown
      const countdown = this.querySelector(".fts-countdown-timer__countdown");
      const heading = this.querySelector(".fts-countdown-timer__heading");
      if (countdown) countdown.style.display = "none";
      if (heading) heading.style.display = "none";
      expiredMessage.style.display = "";
    } else {
      // No expired message configured, hide entire section
      this.style.display = "none";
    }
  }
}

customElements.define("fts-countdown-timer", FtsCountdownTimer);
